<template>
  <quill-editor
    class="editor"
    :class="{ 'editor--auto-grow': autoGrow }"
    ref="myTextEditor"
    :value="content"
    :options="editorOption"
    @change="onEditorChange"
    :disabled="disabled"
  />
</template>

<script>
import debounce from "lodash/debounce";
import { Quill, quillEditor } from "vue-quill-editor";

// highlight.js style
import "highlight.js/styles/tomorrow.css";

// import theme style
import "quill/dist/quill.core.css";
import "quill/dist/quill.snow.css";

const BlockEmbed = Quill.import("blots/block/embed");

class Mp4Video extends BlockEmbed {
  static create(value) {
    const node = super.create();
    node.setAttribute("controls", "controls");
    node.setAttribute("playsinline", "playsinline");
    node.setAttribute("preload", "metadata");
    node.setAttribute("src", this.sanitize(value));
    return node;
  }

  static sanitize(value) {
    return /^(https?):\/\/.+/i.test(value || "") ? value : "about:blank";
  }

  static value(domNode) {
    return domNode.getAttribute("src");
  }
}

Mp4Video.blotName = "mp4-video";
Mp4Video.className = "ql-mp4-video";
Mp4Video.tagName = "video";
Quill.register(Mp4Video, true);

export default {
  name: "quill-example-snow",
  title: "Theme: snow",
  components: {
    quillEditor
  },
  props: {
    text: String,
    language: String,
    field: String,
    disabled: {
      type: Boolean,
      default: false
    },
    autoGrow: {
      type: Boolean,
      default: false
    },
    subproject: {
      type: Number,
      default: null
    }
  },

  computed: {
    editorOption() {
      if (this.disabled) {
        return { modules: { toolbar: [] } };
      } else {
        const editor = this;
        return {
          modules: {
            toolbar: {
              container: [
                ["code-block"],
                ["bold", "italic", "underline", "strike"],
                [{ align: [] }],
                [{ header: 1 }, { header: 2 }],
                [{ list: "ordered" }, { list: "bullet" }],
                //[{ 'indent': '-1' }, { 'indent': '+1' }],
                // [{ 'size': ['small', false, 'large', 'huge'] }],
                [{ header: [1, 2, 3, 4, false] }],
                [{ font: [] }],
                //[{ color: [] }, { background: [] }],
                ["link", "youtube", "mp4"]
              ],
              handlers: {
                youtube() {
                  const url = window.prompt(
                    editor.$t("form.rich_text_video.youtube_prompt")
                  );
                  if (url) {
                    editor.insertYoutube(this.quill, url);
                  }
                },
                mp4() {
                  const url = window.prompt(
                    editor.$t("form.rich_text_video.mp4_prompt")
                  );
                  if (url) {
                    editor.insertMp4(this.quill, url);
                  }
                }
              }
            }
          }
        };
      }
    }
  },
  data() {
    return {
      content: ""
    };
  },
  watch: {
    "$i18n.locale"() {
      this.$nextTick(this.updateVideoButtonLabels);
    },
    text: {
      handler(value) {
        this.content = value || "";
      },
      immediate: true
    }
  },
  mounted() {
    this.$nextTick(this.updateVideoButtonLabels);
  },
  methods: {
    updateVideoButtonLabels() {
      const root = this.$refs.myTextEditor && this.$refs.myTextEditor.$el;
      if (!root) {
        return;
      }
      [
        ["youtube", "form.rich_text_video.youtube_button"],
        ["mp4", "form.rich_text_video.mp4_button"]
      ].forEach(([format, translationKey]) => {
        const button = root.querySelector(`.ql-${format}`);
        if (button) {
          const label = this.$t(translationKey);
          button.setAttribute("aria-label", label);
          button.setAttribute("title", label);
        }
      });
    },
    parseHttpUrl(url) {
      if (!url || typeof url !== "string") {
        return null;
      }
      try {
        const candidate = url.trim();
        const normalized = /^https?:\/\//i.test(candidate)
          ? candidate
          : `https://${candidate}`;
        const parsedUrl = new URL(normalized);
        return ["http:", "https:"].includes(parsedUrl.protocol) ? parsedUrl : null;
      } catch (error) {
        return null;
      }
    },
    youtubeEmbedUrl(url) {
      const parsedUrl = this.parseHttpUrl(url);
      if (!parsedUrl) {
        return null;
      }

      const hostname = parsedUrl.hostname.toLowerCase().replace(/^(www|m)\./, "");
      let videoId = "";
      if (hostname === "youtu.be") {
        videoId = parsedUrl.pathname.split("/").filter(Boolean)[0] || "";
      } else if (["youtube.com", "youtube-nocookie.com"].includes(hostname)) {
        if (parsedUrl.pathname === "/watch") {
          videoId = parsedUrl.searchParams.get("v") || "";
        } else {
          const pathMatch = parsedUrl.pathname.match(/^\/(?:embed|shorts|live)\/([^/?]+)/);
          videoId = pathMatch ? pathMatch[1] : "";
        }
      }

      if (!/^[a-zA-Z0-9_-]+$/.test(videoId)) {
        return null;
      }
      return `https://www.youtube.com/embed/${videoId}`;
    },
    isMp4Url(url) {
      const parsedUrl = this.parseHttpUrl(url);
      return Boolean(parsedUrl && parsedUrl.pathname.toLowerCase().endsWith(".mp4"));
    },
    insertEmbed(quill, blotName, url) {
      const range = quill.getSelection(true);
      if (!range) {
        return;
      }
      const index = range.index;
      quill.deleteText(index, range.length, Quill.sources.SILENT);
      quill.insertEmbed(index, blotName, url, Quill.sources.USER);
      quill.setSelection(index + 2, Quill.sources.SILENT);
    },
    insertYoutube(quill, url) {
      const embedUrl = this.youtubeEmbedUrl(url);
      if (!embedUrl) {
        window.alert(this.$t("form.rich_text_video.invalid_youtube"));
        return;
      }
      this.insertEmbed(quill, "video", embedUrl);
    },
    insertMp4(quill, url) {
      const parsedUrl = this.parseHttpUrl(url);
      if (!this.isMp4Url(url)) {
        window.alert(this.$t("form.rich_text_video.invalid_mp4"));
        return;
      }
      this.insertEmbed(quill, "mp4-video", parsedUrl.toString());
    },
    onEditorChange: debounce(function(value) {
      this.content = value.html;
      this.$emit("change-content", this.language, value.html, this.field);
      this.$emit(
        "change-subproject-content",
        this.language,
        value.html,
        this.field,
        this.subproject
      );
    }, 466)
  }
};
</script>

<style lang="scss" scoped>
.editor {
  height: 15rem;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.editor--auto-grow {
  height: auto;
  min-height: 15rem;
  overflow: visible;
}

.editor--auto-grow ::v-deep .ql-container {
  height: auto;
  flex: 0 0 auto;
  overflow: visible;
}

.editor--auto-grow ::v-deep .ql-editor {
  height: auto;
  min-height: 11rem;
  overflow-y: visible;
}

.editor ::v-deep .ql-toolbar button.ql-youtube,
.editor ::v-deep .ql-toolbar button.ql-mp4 {
  width: auto;
  min-width: 42px;
  padding: 3px 6px;
  font-size: 11px;
  font-weight: 600;
}

.editor ::v-deep .ql-toolbar button.ql-youtube::before {
  content: "YouTube";
}

.editor ::v-deep .ql-toolbar button.ql-mp4::before {
  content: "MP4";
}

.editor ::v-deep .ql-mp4-video {
  display: block;
  width: 100%;
  max-width: 100%;
  height: auto;
}

</style>
